import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { FaBullhorn, FaPlus, FaSearch, FaTrash, FaTimes, FaCheck, FaCheckDouble, FaArrowLeft, FaArrowRight, FaPaperPlane, FaSpinner } from 'react-icons/fa';
import { crmCampaignApi, crmWhatsAppApi } from '../../api/crmApi';
import './CRM.css';

const fallbackCampaigns = [
  { id: 1, name: 'Diwali Greeting 2026', type: 'WhatsApp', template: 'Welcome', status: 'Sent', recipientCount: 120, delivered: 114, read: 96, sentAt: '2026-10-01T10:00:00' },
  { id: 2, name: 'New Product Launch — LPG XL', type: 'WhatsApp', template: 'Product Information', status: 'Scheduled', recipientCount: 80, scheduledAt: '2026-10-05T11:00:00' },
  { id: 3, name: 'Payment Reminder — Oct', type: 'WhatsApp', template: 'Payment Reminder', status: 'Draft', recipientCount: 15 },
];

const fallbackRecipients = [
  { id: 1, name: 'Ankit Mehta', mobile: '9876543210', company: 'Mehta Oxygen Pvt. Ltd.' },
  { id: 2, name: 'Rajesh Kumar', mobile: '9811122233', company: 'Kumar Industrial Gases' },
  { id: 3, name: 'Sunita Devi', mobile: '9765544332', company: 'Devi Gas Agency' },
  { id: 4, name: 'Pooja Sharma', mobile: '9777666555', company: 'Sharma Medical Gas' },
];

const fallbackTemplates = [
  { id: 1, name: 'Welcome', category: 'General', content: 'Hello {{customer_name}}, welcome to CTMS! We supply quality gas cylinders at the best prices.' },
  { id: 2, name: 'Product Information', category: 'Product Information', content: 'Hi {{customer_name}}, our new LPG XL cylinders are now available — 40% longer life. Reply INTEREST for details.' },
  { id: 3, name: 'Payment Reminder', category: 'Payment Reminder', content: 'Dear {{customer_name}}, this is a gentle reminder for your pending payment of {{quotation_amount}}.' },
];

const STEPS = ['Select Template', 'Select Recipients', 'Review & Send'];
const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

export default function CRMBulkMessages() {
  const [campaigns, setCampaigns] = useState(fallbackCampaigns);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [step, setStep] = useState(0);
  const [campaignName, setCampaignName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState(fallbackTemplates);
  const [recipients] = useState(fallbackRecipients);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.allSettled([crmCampaignApi.getAll({ size: 100 }), crmWhatsAppApi.getTemplates()]);
      if (c.status === 'fulfilled') {
        const d = Array.isArray(c.value?.data) ? c.value.data : c.value?.data?.content;
        if (d?.length) setCampaigns(d);
      }
      if (t.status === 'fulfilled') {
        const d = Array.isArray(t.value?.data) ? t.value.data : t.value?.data?.content;
        if (d?.length) setTemplates(d);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleRecipient = (id) => setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const startWizard = () => { setWizard(true); setStep(0); setCampaignName(''); setSelectedTemplate(null); setSelectedIds([]); };

  const sendCampaign = async () => {
    if (!campaignName.trim()) { toast.error('Enter a campaign name.'); return; }
    if (!selectedTemplate) { toast.error('Select a template.'); return; }
    if (!selectedIds.length) { toast.error('Select at least one recipient.'); return; }
    setSending(true);
    const payload = {
      name: campaignName, type: 'WhatsApp', templateId: selectedTemplate.id,
      recipientIds: selectedIds, status: 'Sent', sentAt: new Date().toISOString(),
      recipientCount: selectedIds.length, delivered: selectedIds.length, read: 0,
    };
    try {
      const res = await crmCampaignApi.create(payload);
      const newId = res.data?.id || Date.now();
      setCampaigns((p) => [{ ...payload, id: newId }, ...p]);
      await crmCampaignApi.send(newId).catch(() => {});
      toast.success(`Campaign sent to ${selectedIds.length} recipients.`);
      setWizard(false);
    } catch {
      toast.error('Failed to send campaign — check API configuration.');
    } finally {
      setSending(false);
    }
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try { await crmCampaignApi.delete(c.id); } catch { /* ignore */ }
    setCampaigns((p) => p.filter((x) => x.id !== c.id));
    toast.success('Campaign deleted.');
  };

  const filteredRecipients = recipients.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) || (r.company || '').toLowerCase().includes(q) || (r.mobile || '').includes(q);
  });

  const previewText = selectedTemplate ? selectedTemplate.content
    .replaceAll('{{customer_name}}', 'Ankit')
    .replaceAll('{{company_name}}', 'Mehta Oxygen') : '';

  return (
    <div className="crm-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><FaBullhorn aria-hidden="true" /></div>
          <div>
            <h1 className="crm-title">Bulk Messages</h1>
            <p className="crm-subtitle">WhatsApp campaigns — template → recipients → send</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button className="crm-btn crm-btn-primary" onClick={startWizard}><FaPlus aria-hidden="true" /> New Campaign</button>
        </div>
      </div>

      <div className="crm-table-wrap">
        <div className="crm-table-head"><h3 className="crm-table-title"><FaBullhorn aria-hidden="true" /> Campaigns</h3></div>
        <table className="crm-table">
          <thead><tr><th>Campaign</th><th>Type</th><th>Status</th><th>Recipients</th><th>Delivered</th><th>Read</th><th>Sent / Scheduled</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              Array.from({ length: 3 }).map((_, r) => (
                <tr key={`skeleton-${r}`}>
                  {Array.from({ length: 8 }).map((__, c) => (
                    <td key={c}><SkeletonBlock width={c === 0 ? 150 : '80%'} height={16} radius={6} /></td>
                  ))}
                </tr>
              ))
            ) : (
              <>
            {campaigns.map((c) => (
              <tr key={c.id}>
                <td><strong>{c.name}</strong><div style={{ fontSize: '0.72rem', color: 'var(--ink-soft)' }}>Template: {c.template}</div></td>
                <td>{c.type}</td>
                <td><span className={`crm-badge ${c.status === 'Sent' ? 'crm-badge-won' : c.status === 'Scheduled' ? 'crm-badge-contacted' : 'crm-badge-new'}`}>{c.status}</span></td>
                <td>{c.recipientCount}</td>
                <td>{c.delivered ?? '—'}</td>
                <td>{c.read ?? '—'}</td>
                <td>{fmt(c.sentAt || c.scheduledAt)}</td>
                <td><div className="crm-actions">
                  {c.status === 'Draft' && (
                    <button
                      type="button"
                      className="crm-btn crm-btn-sm crm-btn-primary"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await crmCampaignApi.send(c.id);
                        } catch {
                          toast.error('Failed to send campaign.');
                          return;
                        }
                        setCampaigns((p) => p.map((x) => x.id === c.id ? { ...x, status: 'Sent', sentAt: new Date().toISOString() } : x));
                        toast.success('Campaign sent.');
                      }}
                    >Send</button>
                  )}
                  <button type="button" className="crm-icon-btn lost" title="Delete" aria-label="Delete campaign" onClick={() => deleteCampaign(c)}><FaTrash aria-hidden="true" /></button>
                </div></td>
              </tr>
            ))}
            {!campaigns.length && <tr><td colSpan={8}><div className="crm-empty"><div className="crm-empty-icon"><FaBullhorn aria-hidden="true" /></div><div className="crm-empty-title">No campaigns yet</div><div className="crm-empty-desc">Create your first bulk WhatsApp campaign.</div></div></td></tr>}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* WIZARD */}
      {wizard && createPortal(
        <div className="crm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setWizard(false)}>
          <div className="crm-modal crm-modal-xl" role="dialog" aria-modal="true" aria-label="New bulk campaign">
            <div className="crm-modal-header">
              <h3><FaBullhorn aria-hidden="true" /> New Bulk Campaign</h3>
              <button type="button" className="crm-modal-close" onClick={() => setWizard(false)} aria-label="Close dialog"><FaTimes aria-hidden="true" /></button>
            </div>

            <div className="crm-steps">
              {STEPS.map((s, i) => (
                <div key={s} className={`crm-step ${step === i ? 'active' : step > i ? 'done' : ''}`}>
                  <span className="crm-step-num">{step > i ? <FaCheck aria-hidden="true" /> : i + 1}</span> {s}
                </div>
              ))}
            </div>

            <div className="crm-modal-body">
              {step === 0 && (
                <div>
                  <div className="crm-field" style={{ marginBottom: 16 }}>
                    <label>Campaign Name <span className="req">*</span></label>
                    <input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="e.g. Diwali Offer 2026" />
                  </div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--ink-soft)' }}>Select Template</label>
                  <div className="crm-list-grid" style={{ marginTop: 8 }}>
                    {templates.map((t) => (
                      <div key={t.id} className={`crm-list-card ${selectedTemplate?.id === t.id ? 'selected' : ''}`}
                        style={{ cursor: 'pointer', borderColor: selectedTemplate?.id === t.id ? 'var(--primary)' : undefined }}
                        onClick={() => setSelectedTemplate(t)}>
                        <div className="crm-list-card-title">{t.name}</div>
                        <div className="crm-list-card-sub">{t.category}</div>
                        <div style={{ fontSize: '0.76rem', color: 'var(--ink-soft)', marginTop: 6 }}>{t.content.slice(0, 80)}...</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <div className="crm-filters" style={{ marginBottom: 12 }}>
                    <div className="crm-search-box">
                      <FaSearch className="crm-search-icon" aria-hidden="true" />
                      <input
                        type="text"
                        aria-label="Search recipients"
                        autoComplete="off"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search recipients..."
                      />
                    </div>
                    <button className="crm-btn crm-btn-sm crm-btn-ghost" onClick={() => setSelectedIds(filteredRecipients.map((r) => r.id))}>Select All</button>
                    <button className="crm-btn crm-btn-sm crm-btn-ghost" onClick={() => setSelectedIds([])}>Clear</button>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--primary)' }}>{selectedIds.length} selected</span>
                  </div>
                  <table className="crm-table">
                    <thead><tr><th style={{ width: 40 }}></th><th>Name</th><th>Company</th><th>Mobile</th></tr></thead>
                    <tbody>
                      {filteredRecipients.map((r) => (
                        <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => toggleRecipient(r.id)}>
                          <td><input type="checkbox" className="crm-checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleRecipient(r.id)} /></td>
                          <td><strong>{r.name}</strong></td>
                          <td>{r.company}</td>
                          <td>{r.mobile}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {step === 2 && (
                <div>
                  <div className="crm-panel-card" style={{ marginBottom: 14 }}>
                    <div className="crm-panel-card-title">Campaign Summary</div>
                    <div className="crm-info-row"><span>Name</span><strong>{campaignName || '—'}</strong></div>
                    <div className="crm-info-row"><span>Template</span><strong>{selectedTemplate?.name || '—'}</strong></div>
                    <div className="crm-info-row"><span>Recipients</span><strong>{selectedIds.length}</strong></div>
                  </div>
                  <div className="crm-panel-card">
                    <div className="crm-panel-card-title">Message Preview</div>
                    <div style={{ background: '#DCF8C6', borderRadius: 14, padding: '10px 14px', fontSize: '0.83rem', maxWidth: 420, position: 'relative' }}>
                      {previewText || 'Select a template to preview...'}
                      <div style={{ fontSize: '0.66rem', textAlign: 'right', opacity: 0.6, marginTop: 4, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
                        now <FaCheckDouble aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="crm-modal-footer">
              <button type="button" className="crm-btn crm-btn-ghost" onClick={() => step === 0 ? setWizard(false) : setStep(step - 1)}>
                {step === 0 ? 'Cancel' : <><FaArrowLeft aria-hidden="true" /> Back</>}
              </button>
              {step < 2 ? (
                <button type="button" className="crm-btn crm-btn-primary"
                  onClick={() => {
                    if (step === 0 && (!campaignName.trim() || !selectedTemplate)) { toast.error('Enter name and select a template.'); return; }
                    if (step === 1 && !selectedIds.length) { toast.error('Select at least one recipient.'); return; }
                    setStep(step + 1);
                  }}>Next <FaArrowRight aria-hidden="true" /></button>
              ) : (
                <button type="button" className="crm-btn crm-btn-primary" onClick={sendCampaign} disabled={sending}>
                  {sending ? <><FaSpinner className="crm-spin" aria-hidden="true" /> Sending...</> : <><FaPaperPlane aria-hidden="true" /> Send to {selectedIds.length} recipients</>}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
